import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

type FilterDefs = Record<string, string>;

/**
 * Manages filter state synchronized with URL search params.
 *
 * IMPORTANT: The `defaults` object must be referentially stable — define it
 * as a module-level constant outside the component, or wrap it in useMemo.
 * An unstable reference will cause infinite render loops.
 */
export default function useFilterParams<T extends FilterDefs>(
  defaults: T,
): [T, (key: keyof T, value: string) => void] {
  const [searchParams, setSearchParams] = useSearchParams();

  const values = useMemo(() => {
    const result = { ...defaults };
    for (const key of Object.keys(defaults)) {
      const val = searchParams.get(key);
      if (val !== null) {
        (result as Record<string, string>)[key] = val;
      }
    }
    return result;
  }, [searchParams, defaults]);

  const setFilter = useCallback(
    (key: keyof T, value: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value === defaults[key] || value === "") {
            next.delete(key as string);
          } else {
            next.set(key as string, value);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams, defaults],
  );

  return [values, setFilter];
}
